import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { LeadOutcome, LeadPriority } from "@/lib/db-types";
import type { LeadPayload, LogLeadAttemptInput } from "@/hooks/use-leads";

/** Ett kort på Leads-tavlen (RPC lead_board, VIB-3050). */
export interface BoardCard {
  id: string;
  name: string;
  municipality: string | null;
  employees: number | null;
  priority: LeadPriority | null;
  status: string;
  last_outcome: LeadOutcome | null;
  last_attempt_at: string | null;
  attempts_count: number;
  /** E-postdato, ringetidspunkt eller møtetid, etter søyle. */
  due_at: string | null;
  /** Verten for et booket møte. */
  host_name: string | null;
  note: string | null;
  contact_name: string | null;
  phone: string | null;
}

export interface LeadBoardData {
  /** VIB-3050: ringbare i køen mot lavvann og høyvann. */
  queue: { callable: number; low_water: number; high_water: number };
  to_contact: BoardCard[];
  to_contact_total: number;
  to_email: BoardCard[];
  to_call: BoardCard[];
  booked: BoardCard[];
}

/** Bookerens egen tavle. Bare egne leads og køen bookeren kan få. */
export function useLeadBoard(options: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["lead-board", user?.id],
    queryFn: async (): Promise<LeadBoardData> => {
      const { data, error } = await supabase.rpc("lead_board");
      if (error) throw new Error(error.message);
      return data as unknown as LeadBoardData;
    },
    enabled: !!user?.id && (options.enabled ?? true),
  });
}

async function takeLead(leadId: string): Promise<LeadPayload> {
  const { data, error } = await supabase.rpc("take_lead", { p_lead_id: leadId });
  if (error) throw new Error(error.message);
  return data as unknown as LeadPayload;
}

async function invalidateLeads(qc: ReturnType<typeof useQueryClient>, userId: string | undefined) {
  await invalidateFor(qc, "lead_attempts", { leadId: undefined });
  await invalidateFor(qc, "leads", { userId });
}

/** Låser et lead fra tavlen, for eksempel før booking. Gir payloaden med kontaktene. */
export function useTakeLead() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: takeLead,
    onSuccess: () => invalidateFor(qc, "leads", { userId: user?.id }),
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Utfall fra tavlen: låser leadet og logger utfallet, som tasten på leadkortet. */
export function useBoardOutcome() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: LogLeadAttemptInput) => {
      await takeLead(input.leadId);
      const { error } = await supabase.rpc("log_lead_attempt", {
        p_lead_id: input.leadId,
        p_outcome: input.outcome,
        p_note: input.note ?? null,
        p_call_back_at: input.callBackAt ?? null,
        p_reason: input.reason ?? null,
        p_revisit_on: input.revisitOn ?? null,
      });
      if (error) throw new Error(error.message);
    },
    // Ikke vent på oppfriskingen: da ville tavlen vært låst (pending) til alle avhengige
    // spørringer var hentet, og kortene kunne ikke dras i mellomtiden.
    onSuccess: () => void invalidateLeads(qc, user?.id),
    onError: (err: Error) => toast.error(err.message),
  });
}

/** «Tilbake til poolen»: leadet blir ledig for alle igjen. */
export function useReturnLeadToPool() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (leadId: string) => {
      const { error } = await supabase.rpc("return_lead_to_pool", { p_lead_id: leadId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void invalidateFor(qc, "leads", { userId: user?.id }),
    onError: (err: Error) => toast.error(err.message),
  });
}
